import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company`, settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Company Settings</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition disabled:opacity-50"
          data-testid="save-settings-button"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Company Logo Preview */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Logo
          </label>
          <div className="flex items-center space-x-4">
            <img 
              src={settings.logo_url} 
              alt="Company Logo" 
              className="h-20 border border-gray-300 rounded p-2"
            />
            <div className="text-sm text-gray-600">
              <p>Logo Path: {settings.logo_url}</p>
              <p className="text-xs text-gray-500 mt-1">To change logo, replace the file at /app/frontend/public/assets/company-logo.png</p>
            </div>
          </div>
        </div>

        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Name *
          </label>
          <input
            type="text"
            value={settings.company_name}
            onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            required
            data-testid="company-name-input"
          />
        </div>

        {/* Tagline */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tagline
          </label>
          <input
            type="text"
            value={settings.tagline || ''}
            onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g., Precision Fabrication for a Global Market"
            data-testid="tagline-input"
          />
        </div>

        {/* Certification */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Certifications
          </label>
          <input
            type="text"
            value={settings.certification || ''}
            onChange={(e) => setSettings({ ...settings, certification: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g., ISO 9001:2015 Certified"
            data-testid="certification-input"
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address *
          </label>
          <textarea
            value={settings.address}
            onChange={(e) => setSettings({ ...settings, address: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            rows="3"
            required
            data-testid="address-input"
          />
        </div>

        {/* GST Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            GST Number
          </label>
          <input
            type="text"
            value={settings.gst_number || ''}
            onChange={(e) => setSettings({ ...settings, gst_number: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g., 03AATPM8824D1ZK"
            data-testid="gst-input"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email *
          </label>
          <input
            type="email"
            value={settings.email}
            onChange={(e) => setSettings({ ...settings, email: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            required
            data-testid="email-input"
          />
        </div>

        {/* Phone Numbers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Numbers
          </label>
          {settings.phone_numbers && settings.phone_numbers.map((phone, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                value={phone}
                onChange={(e) => {
                  const newPhones = [...settings.phone_numbers];
                  newPhones[index] = e.target.value;
                  setSettings({ ...settings, phone_numbers: newPhones });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="+91XXXXXXXXXX"
                data-testid={`phone-input-${index}`}
              />
              <button
                onClick={() => {
                  const newPhones = settings.phone_numbers.filter((_, i) => i !== index);
                  setSettings({ ...settings, phone_numbers: newPhones });
                }}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              setSettings({ 
                ...settings, 
                phone_numbers: [...(settings.phone_numbers || []), ''] 
              });
            }}
            className="mt-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="add-phone-button"
          >
            + Add Phone Number
          </button>
        </div>

        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brand Color
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={settings.primary_color || '#dc2626'}
              onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
              className="h-10 w-20 rounded border border-gray-300"
              data-testid="color-input"
            />
            <input
              type="text"
              value={settings.primary_color || '#dc2626'}
              onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="#dc2626"
            />
            <div className="text-sm text-gray-600">
              Current: <span className="font-semibold" style={{ color: settings.primary_color }}>{settings.primary_color}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Company Info Display */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information Preview</h3>
        <div className="space-y-2 text-sm">
          <p><strong>Name:</strong> {settings.company_name}</p>
          {settings.tagline && <p><strong>Tagline:</strong> {settings.tagline}</p>}
          {settings.certification && <p><strong>Certification:</strong> {settings.certification}</p>}
          <p><strong>Address:</strong> {settings.address}</p>
          {settings.gst_number && <p><strong>GST:</strong> {settings.gst_number}</p>}
          <p><strong>Email:</strong> {settings.email}</p>
          {settings.phone_numbers && settings.phone_numbers.length > 0 && (
            <p><strong>Phones:</strong> {settings.phone_numbers.join(', ')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
